import { trackOutputAction } from 'analytics';
import forge from 'apis/forge';
import Modal from 'components/Modal';
import { ExportFramesAiPreset, MAX_LLM_EXPORT_FRAMES } from 'dragonmouse-shared/api/export';
import { Frame } from 'dragonmouse-shared/interfaces';
import { useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectActiveScene, selectFrames } from 'redux/db/reducers';
import { hideExportFramesAiModal } from 'redux/mode/actions';
import { RootReducer, selectSelectedFrames } from 'redux/reducers';
import store from 'redux/store';
import { runExportWithToast } from 'components/Widget/ExportReadyToast';
import { buildExportFilename } from 'utils';
import * as Sentry from '@sentry/react';
import RadioCard from 'components/Widget/RadioCard';
import { ExportScope, resolveFrameIds, scopeText } from '../util';
import * as Shared from '../styles';
import * as S from './styles';

interface Props {
	fixedFrameIds?: string[];
	onClose: () => void;
}

const PRESET_CARDS: Array<{
	preset: ExportFramesAiPreset;
	label: string;
	description: string;
}> = [
	{ preset: 'spec', label: 'Spec / PRD', description: 'Requirements-style product document' },
	{
		preset: 'style',
		label: 'Style guide',
		description: 'Look, feel, and voice distilled from the mockups',
	},
	{ preset: 'custom', label: 'Custom', description: 'Describe what you want' },
];

const FramesAiModal = ({ fixedFrameIds, onClose }: Props) => {
	const { selectedFrames, allFrames, activeScene } = useSelector(
		(r: RootReducer) => ({
			selectedFrames: selectSelectedFrames(r),
			allFrames: selectFrames(r.db),
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
		}),
		shallowEqual
	);

	const initialScope: ExportScope = selectedFrames.length > 0 ? 'selected' : 'all';
	const [scope] = useState<ExportScope>(initialScope);
	const [preset, setPreset] = useState<ExportFramesAiPreset>('spec');
	const [brief, setBrief] = useState('');
	const [submitted, setSubmitted] = useState(false);

	const activeFrames: Frame[] = useMemo(() => {
		const ids = resolveFrameIds(fixedFrameIds, scope, selectedFrames, allFrames);
		return ids.map((id) => allFrames.find((f) => f.id === id)).filter((f): f is Frame => !!f);
	}, [fixedFrameIds, scope, selectedFrames, allFrames]);

	const frameCount = activeFrames.length;
	const overFrameCap = frameCount > MAX_LLM_EXPORT_FRAMES;

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const closeModal = () => {
		store.dispatch(hideExportFramesAiModal());
		onClose();
	};

	const onGenerate = async () => {
		if (frameCount === 0) return;
		if (overFrameCap) return;
		if (preset === 'custom' && !brief.trim()) {
			toast.error('Describe what you want in the brief');
			return;
		}
		if (!activeScene) return;
		setSubmitted(true);
		trackOutputAction('export_frames_ai');
		closeModal();

		const filename = buildExportFilename(activeScene.title, preset, 'document', 'md');
		await runExportWithToast({
			pendingLabel: 'Writing your .md — this may take a minute or two…',
			run: async () => {
				const { exportId } = await forge.exportFramesAi({
					frameIds: activeFrames.map((f) => f.id),
					preset,
					customBrief: brief.trim() || undefined,
					filename,
				});
				return { exportId, filename };
			},
		}).catch((e) => Sentry.captureException(e));
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const submitDisabled =
		submitted || frameCount === 0 || overFrameCap || (preset === 'custom' && !brief.trim());

	const footerInfo = !fixedFrameIds ? (
		<div>{scopeText(selectedFrames.length, allFrames.length)}</div>
	) : undefined;

	return (
		<Modal
			title="Export for AI"
			description="Get .MD files for Claude Code, ChatGPT, Codex, Cursor, etc."
			onClose={closeModal}
			onCancel={closeModal}
			onSubmit={onGenerate}
			submitLabel="Download"
			disabled={submitDisabled}
			loading={false}
			footerInfo={footerInfo}
		>
			<Shared.Body>
				<S.PresetGrid>
					{PRESET_CARDS.map((card) => (
						<RadioCard
							key={card.preset}
							active={preset === card.preset}
							label={card.label}
							description={card.description}
							onClick={() => setPreset(card.preset)}
						/>
					))}
				</S.PresetGrid>
				<Shared.OptionsGroup>
					<S.Textarea
						placeholder={
							preset === 'custom'
								? 'Describe what you want — e.g. a UX write-up, user journey, content extraction…'
								: 'Optional: any specific angle, focus, or detail you want emphasized.'
						}
						value={brief}
						onChange={(e) => setBrief(e.target.value)}
					/>
				</Shared.OptionsGroup>
			</Shared.Body>
		</Modal>
	);
};

export default FramesAiModal;
